import React from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';

import Navigation from '../Navigation';
import Landing from '../Landing';

import CalculatorPage from '../Calculator';

import Replan from '../Replan/Replan.js';
import PdfStreamTest from '../Replan/components/PdfStreamTest.js';
import ShapeListStandalone from '../ShapeTakeoff';

import SignUpPage from '../SignUp';
import SignInPage from '../SignIn';
import PasswordForgetPage from '../PasswordForget';
import HomePage from '../Home';
import AccountPage from '../Account';
import AdminPage from '../Admin';
// Lazy: VControl pulls in the heavy upload workflow (tesseract.js, pdf.js,
// VControl.css, ROI canvas). Loading it on demand keeps the initial bundle
// small and Replan's page lean.
const VControl = React.lazy(() => import('../VControl/VControl'));
import InventoryProvider from '../Inventory/InventoryProvider';

import BlogPage from '../Blog';
import ArticlePage from '../Blog/Article';
import BlogAdminPage from '../Blog/BlogAdmin';
import FeatureCompletionDashboard from '../FeatureCompletionDashboard/FeatureCompletionDashboard';
import GanttPlanning from '../GanttPlanning';
import VerifyEmailActionPage from '../Session/VerifyEmailActionPage';
import ResetPasswordActionPage from '../Session/ResetPasswordActionPage';
import NotFound from '../NotFound';

import Landing360 from '../Landing/Landing360.js';
import WaitlistConfirm from '../WaitlistConfirm';
import { ProjectSelectionProvider } from '../ProjectSelection';

import * as ROUTES from '../../constants/routes';
import { withAuthentication } from '../Session';
import useGlobalTooltips from '../../hooks/useGlobalTooltips';

// Project selection context is intentionally mounted only for modules
// that need a shared selected project state.
const PROJECT_AWARE_ROUTES = new Set([
  ROUTES.REPLAN,
  ROUTES.SHAPELIST,
  ROUTES.GANTT_PLANNING,
  ROUTES.PDF_STREAM_TEST,
  ROUTES.HOME,
]);

const AppRoutes = ({ restrictToLanding }) => {
  const location = useLocation();
  useGlobalTooltips();
  // Exact-path matching by design (requested). If module routes become nested
  // later (e.g. /replan/*), update this check to a startsWith matcher.
  const needsProjectSelection = PROJECT_AWARE_ROUTES.has(location.pathname);

  // Landing360 owns the viewport, so hide the global nav on the landing route.
  const EXPLICIT_360_ROUTES = new Set([
    ROUTES.BLOG_ARTICLE,
    ROUTES.BLOG,
    ROUTES.SIGN_UP,
    ROUTES.SIGN_IN,
    ROUTES.PASSWORD_FORGET,
    ROUTES.AUTH_VERIFY,
    ROUTES.AUTH_RESET_PASSWORD,
    ROUTES.VCONTROL,
    ROUTES.REPLAN,
    ROUTES.PDF_STREAM_TEST,
    ROUTES.SHAPELIST,
    ROUTES.WAITLIST_CONFIRM,
  ]);
  const isLandingPage = restrictToLanding
    ? !EXPLICIT_360_ROUTES.has(location.pathname)
    : location.pathname === ROUTES.LANDING;

  if (restrictToLanding) {
    return (
      <>
        {!isLandingPage && <Navigation />}
        <Routes>
          {/* Route everything to Landing360 when running on localhost:360 except /blog and /blog/* */}
          <Route path={ROUTES.BLOG_ARTICLE} element={<ArticlePage />} />
          <Route path={ROUTES.BLOG} element={<BlogPage />} />
          <Route path={ROUTES.SIGN_UP} element={<SignUpPage />} />
          <Route path={ROUTES.SIGN_IN} element={<SignInPage />} />
          <Route path={ROUTES.PASSWORD_FORGET} element={<PasswordForgetPage />} />
          <Route path={ROUTES.AUTH_VERIFY} element={<VerifyEmailActionPage />} />
          <Route path={ROUTES.AUTH_RESET_PASSWORD} element={<ResetPasswordActionPage />} />
          <Route path={ROUTES.VCONTROL} element={<React.Suspense fallback={null}><VControl /></React.Suspense>} />
          <Route path={ROUTES.REPLAN} element={<Replan />} />
          <Route path={ROUTES.PDF_STREAM_TEST} element={<PdfStreamTest />} />
          <Route path={ROUTES.SHAPELIST} element={<ShapeListStandalone />} />
          <Route path={ROUTES.WAITLIST_CONFIRM} element={<WaitlistConfirm />} />
          <Route path="*" element={<Landing360 />} />
        </Routes>
      </>
    );
  }

  const content = (
    <>
      {!isLandingPage && <Navigation />}
      <Routes>
        <Route exact path={ROUTES.LANDING} element={<Landing360 />} />
        <Route path={ROUTES.CALCULATOR} element={<CalculatorPage />} />
        <Route path={ROUTES.SIGN_UP} element={<SignUpPage />} />
        <Route path={ROUTES.SIGN_IN} element={<SignInPage />} />
        <Route path={ROUTES.PASSWORD_FORGET} element={<PasswordForgetPage />} />
        <Route path={ROUTES.AUTH_VERIFY} element={<VerifyEmailActionPage />} />
        <Route path={ROUTES.AUTH_RESET_PASSWORD} element={<ResetPasswordActionPage />} />
        <Route path={ROUTES.HOME} element={<HomePage />} />
        <Route path={ROUTES.ACCOUNT} element={<AccountPage />} />
        <Route path={ROUTES.ADMIN} element={<AdminPage />} />
        <Route path={ROUTES.VCONTROL} element={<React.Suspense fallback={null}><VControl /></React.Suspense>} />
        <Route path={ROUTES.REPLAN} element={<Replan />} />
        <Route path={ROUTES.PDF_STREAM_TEST} element={<PdfStreamTest />} />
        <Route path={ROUTES.SHAPELIST} element={<ShapeListStandalone />} />
          <Route path={`${ROUTES.INVENTORY}/*`} element={<InventoryProvider />} />
        <Route path={ROUTES.BLOG_ADMIN} element={<BlogAdminPage />} />
        <Route path={ROUTES.BLOG_ARTICLE} element={<ArticlePage />} />
        <Route path={ROUTES.BLOG} element={<BlogPage />} />
        <Route path={ROUTES.PRICING} element={<FeatureCompletionDashboard />} />
        <Route path={ROUTES.GANTT_PLANNING} element={<GanttPlanning />} />
        <Route path={ROUTES.WAITLIST_CONFIRM} element={<WaitlistConfirm />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );

  // Routes outside the project-aware set render without ProjectSelectionProvider.
  if (!needsProjectSelection) {
    return content;
  }

  // Shared selector state is available only for project-aware modules.
  return <ProjectSelectionProvider>{content}</ProjectSelectionProvider>;
};


const App = () => {
  // Detect if the current browser URL contains the dev port (localhost:360)
  const href = typeof window !== 'undefined' && window.location ? window.location.href : '';
  const restrictToLanding = false
  // const restrictToLanding = href.includes('360');

  return (
    <Router>
      <AppRoutes restrictToLanding={restrictToLanding} />
    </Router>
  );
};

export default withAuthentication(App);
